import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Users, Store, Package, Plus, Search, Pencil, Trash2, X, Save,
  Phone, MapPin, AlertCircle, FileText, Upload, ExternalLink, Eye,
} from "lucide-react";
import api, { fileUrl } from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Stage 4D (Feb 2026): Super Admin INFO Section
//   Sub-tabs: Employee Info • Shop Info • Product Info
//   Full CRUD + search + audit-logged. Super Admin only for write actions.

const TABS = [
  { id: "employee", en: "Employee Info", ta: "ஊழியர் தகவல்",  icon: Users,   color: "bg-[#0055FF]" },
  { id: "shop",     en: "Shop Info",     ta: "கடை தகவல்",     icon: Store,   color: "bg-[#34C759]" },
  { id: "product",  en: "Product Info",  ta: "பொருள் தகவல்",  icon: Package, color: "bg-[#5856D6]" },
];

const ID_PROOF_TYPES = [
  { id: "aadhaar",         en: "Aadhaar",          ta: "ஆதார்" },
  { id: "driving_license", en: "Driving License",  ta: "ஓட்டுநர் உரிமம்" },
  { id: "voter_id",        en: "Voter ID",         ta: "வாக்காளர் அடையாளம்" },
  { id: "other",           en: "Other",            ta: "மற்றவை" },
];

export default function AdminInfo() {
  const { lang } = useLang();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = TABS.find(t => t.id === params.get("tab"))?.id || "employee";
  const [tab, setTab] = useState(initialTab);

  const setTabAndUrl = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-12">
      <TopBar title={lang === "ta" ? "தகவல்" : "Info"} onBack={() => nav("/admin")} />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        <div className="flex gap-2 bg-white rounded-2xl p-1.5 soft-shadow overflow-x-auto" data-testid="info-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} data-testid={`info-tab-${t.id}`} onClick={() => setTabAndUrl(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition ${active ? `${t.color} text-white shadow-md` : "text-[#1D1D1F] hover:bg-gray-50"}`}>
                <Icon className="w-4 h-4" />
                {lang === "ta" ? t.ta : t.en}
              </button>
            );
          })}
        </div>

        {tab === "employee" && <EmployeeTab lang={lang} isSuperAdmin={isSuperAdmin} />}
        {tab === "shop"     && <ShopTab     lang={lang} isSuperAdmin={isSuperAdmin} />}
        {tab === "product"  && <ProductTab  lang={lang} isSuperAdmin={isSuperAdmin} />}
      </div>
    </div>
  );
}

// =================== EMPLOYEE TAB ===================
const EmployeeTab = ({ lang, isSuperAdmin }) => {
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const reload = () => {
    Promise.all([
      api.get("/users", { params: { status: "active" } }).then(r => r.data || []),
      api.get("/districts").then(r => r.data || []),
    ]).then(([u, d]) => { setUsers(u); setDistricts(d); });
  };
  useEffect(reload, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (!["salesman", "manager"].includes(u.role)) return false;
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return [u.name, u.name_ta, u.phone, u.address, u.emergency_contact].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  const askDelete = async (u) => {
    const msg = lang === "ta" ? `${u.name} நீக்கவா?` : `Are you sure you want to delete ${u.name}?\nAction will be logged.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(lang === "ta" ? "நீக்கப்பட்டது" : "Deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4 fade-up">
      <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3 flex-wrap" data-testid="emp-toolbar">
        <div className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl px-3 py-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#86868B]" />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="emp-search"
            placeholder={lang === "ta" ? "பெயர் / தொலைபேசி தேடு…" : "Search name / phone…"}
            className="bg-transparent flex-1 outline-none text-sm font-semibold" />
        </div>
        <div className="flex gap-1.5 bg-[#F5F5F7] rounded-xl p-1">
          {["all", "salesman", "manager"].map(r => (
            <button key={r} onClick={() => setRoleFilter(r)} data-testid={`emp-filter-${r}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${roleFilter === r ? "bg-white shadow text-[#0055FF]" : "text-[#86868B]"}`}>
              {r === "all" ? (lang === "ta" ? "அனைத்தும்" : "All") : r === "salesman" ? (lang === "ta" ? "சேல்ஸ்மென்" : "Salesmen") : (lang === "ta" ? "மேனேஜர்கள்" : "Managers")}
            </button>
          ))}
        </div>
        {isSuperAdmin && (
          <button onClick={() => setAdding(true)} data-testid="emp-add-btn"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> {lang === "ta" ? "சேர்" : "Add Employee"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="emp-grid">
        {visible.map(u => {
          const dist = districtMap[u.district_id];
          return (
            <div key={u.id} className="bg-white rounded-2xl p-5 soft-shadow flex flex-col gap-2" data-testid={`emp-card-${u.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${u.role === "manager" ? "text-[#5856D6]" : "text-[#FF9500]"}`}>
                    {u.role === "manager" ? (lang === "ta" ? "மேனேஜர்" : "Manager") : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman")}
                  </div>
                  <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">
                    {lang === "ta" && u.name_ta ? u.name_ta : u.name}
                  </div>
                  {dist && <div className="text-xs text-[#86868B] truncate">{lang === "ta" && dist.name_ta ? dist.name_ta : dist.name}</div>}
                </div>
                {isSuperAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(u)} data-testid={`emp-edit-${u.id}`}
                      className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]" title={lang === "ta" ? "திருத்து" : "Edit"}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => askDelete(u)} data-testid={`emp-delete-${u.id}`}
                      className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]" title={lang === "ta" ? "நீக்கு" : "Delete"}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-[#1D1D1F]"><Phone className="w-3.5 h-3.5 text-[#86868B]" /> {u.phone}</div>
                {u.address && <div className="flex items-start gap-2 text-[#1D1D1F]"><MapPin className="w-3.5 h-3.5 text-[#86868B] mt-0.5" /> <span className="truncate">{u.address}</span></div>}
                {u.emergency_contact && <div className="flex items-center gap-2 text-[#FF3B30]"><AlertCircle className="w-3.5 h-3.5" /> {u.emergency_contact}</div>}
                {u.id_proof_path && (
                  <a href={fileUrl(u.id_proof_path)} target="_blank" rel="noreferrer" data-testid={`emp-id-proof-${u.id}`}
                    className="inline-flex items-center gap-2 text-[#5856D6] font-bold text-xs hover:underline">
                    <FileText className="w-3.5 h-3.5" /> {ID_PROOF_TYPES.find(t => t.id === u.id_proof_type)?.[lang] || (lang === "ta" ? "அடையாள ஆவணம்" : "ID Proof")}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <div className="col-span-full text-center text-[#86868B] py-12 bg-white rounded-2xl">{lang === "ta" ? "ஊழியர் இல்ல" : "No employees"}</div>}
      </div>

      {editing && <EmployeeModal user={editing} districts={districts} lang={lang}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {adding && <EmployeeModal user={null} districts={districts} lang={lang}
        onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}
    </div>
  );
};

const EmployeeModal = ({ user, districts, lang, onClose, onSaved }) => {
  const isNew = !user;
  const [form, setForm] = useState({
    name: user?.name || "",
    name_ta: user?.name_ta || "",
    role: user?.role || "salesman",
    phone: user?.phone || "",
    pin: "",
    district_id: user?.district_id || (districts[0]?.id || ""),
    address: user?.address || "",
    emergency_contact: user?.emergency_contact || "",
    id_proof_type: user?.id_proof_type || "aadhaar",
    id_proof_path: user?.id_proof_path || null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("id_proof_path", r.data.path);
      toast.success(lang === "ta" ? "ஏற்றப்பட்டது" : "Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error(lang === "ta" ? "பெயர் தேவை" : "Name required");
    if (!form.name_ta.trim()) return toast.error(lang === "ta" ? "தமிழ் பெயர் தேவை" : "Tamil name required");
    if (!form.phone.trim()) return toast.error(lang === "ta" ? "தொலைபேசி தேவை" : "Phone required");
    setSaving(true);
    try {
      const payload = {
        name: form.name, name_ta: form.name_ta, role: form.role, phone: form.phone,
        district_id: form.district_id || null, address: form.address,
        emergency_contact: form.emergency_contact,
        id_proof_type: form.id_proof_type, id_proof_path: form.id_proof_path,
      };
      if (isNew) {
        if (!form.pin) payload.pin = "1234";
        else payload.pin = form.pin;
        await api.post("/users", payload);
      } else {
        if (form.pin) payload.pin = form.pin;
        delete payload.role; // role change via separate flow
        await api.put(`/users/${user.id}`, payload);
      }
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={isNew ? (lang === "ta" ? "புதிய ஊழியர்" : "New Employee") : (lang === "ta" ? "ஊழியர் திருத்து" : "Edit Employee")}
      sub={isNew ? "" : user.phone} onClose={onClose} testid="emp-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label={lang === "ta" ? "பெயர் (English)" : "Name (English)"} value={form.name} onChange={v => set("name", v)} testid="emp-form-name" />
        <Field label={lang === "ta" ? "தமிழ் பெயர்" : "Tamil Name"} value={form.name_ta} onChange={v => set("name_ta", v)} testid="emp-form-name-ta" />
        <div>
          <Lbl>{lang === "ta" ? "பதவி" : "Designation"}</Lbl>
          <select value={form.role} onChange={e => set("role", e.target.value)} disabled={!isNew} data-testid="emp-form-role"
            className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold disabled:opacity-60">
            <option value="salesman">{lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"}</option>
            <option value="manager">{lang === "ta" ? "மேனேஜர்" : "Manager"}</option>
          </select>
        </div>
        <div>
          <Lbl>{lang === "ta" ? "மாவட்டம்" : "District"}</Lbl>
          <select value={form.district_id} onChange={e => set("district_id", e.target.value)} data-testid="emp-form-district"
            className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold">
            <option value="">—</option>
            {districts.map(d => <option key={d.id} value={d.id}>{lang === "ta" && d.name_ta ? d.name_ta : d.name}</option>)}
          </select>
        </div>
        <Field label={lang === "ta" ? "தொலைபேசி" : "Phone"} value={form.phone} onChange={v => set("phone", v)} testid="emp-form-phone" type="tel" />
        <Field label={lang === "ta" ? `PIN ${isNew ? "(விருப்பம்)" : "(மாற்ற மட்டும்)"}` : `PIN ${isNew ? "(default 1234)" : "(only to change)"}`} value={form.pin} onChange={v => set("pin", v)} testid="emp-form-pin" type="password" />
      </div>
      <Field label={lang === "ta" ? "முகவரி" : "Address"} value={form.address} onChange={v => set("address", v)} testid="emp-form-address" multi />
      <Field label={lang === "ta" ? "அவசர தொடர்பு" : "Emergency Contact"} value={form.emergency_contact} onChange={v => set("emergency_contact", v)} testid="emp-form-emergency" type="tel" />

      <div>
        <Lbl>{lang === "ta" ? "அடையாள ஆவணம்" : "ID Proof"}</Lbl>
        <div className="flex gap-2 flex-wrap">
          <select value={form.id_proof_type} onChange={e => set("id_proof_type", e.target.value)} data-testid="emp-form-id-type"
            className="bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold">
            {ID_PROOF_TYPES.map(t => <option key={t.id} value={t.id}>{lang === "ta" ? t.ta : t.en}</option>)}
          </select>
          <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" data-testid="emp-form-id-file"
            onChange={e => upload(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="emp-form-upload-btn"
            className="bg-white border border-gray-200 hover:bg-gray-50 rounded-xl px-3 py-2.5 text-sm font-bold flex items-center gap-1.5">
            <Upload className="w-4 h-4" /> {uploading ? "…" : (form.id_proof_path ? (lang === "ta" ? "மாற்று" : "Replace") : (lang === "ta" ? "ஏற்று" : "Upload"))}
          </button>
          {form.id_proof_path && (
            <a href={fileUrl(form.id_proof_path)} target="_blank" rel="noreferrer" data-testid="emp-form-view-id"
              className="bg-white border border-gray-200 hover:bg-gray-50 rounded-xl px-3 py-2.5 text-sm font-bold flex items-center gap-1.5 text-[#5856D6]">
              <Eye className="w-4 h-4" /> {lang === "ta" ? "காண்" : "View"}
            </a>
          )}
        </div>
      </div>

      <SaveBtn saving={saving} onClick={submit} lang={lang} testid="emp-form-save" />
    </ModalShell>
  );
};

// =================== SHOP TAB ===================
const ShopTab = ({ lang, isSuperAdmin }) => {
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const reload = () => {
    Promise.all([
      api.get("/shops").then(r => r.data || []),
      api.get("/users", { params: { status: "active" } }).then(r => r.data || []),
      api.get("/districts").then(r => r.data || []),
    ]).then(([s, u, d]) => { setShops(s); setUsers(u); setDistricts(d); });
  };
  useEffect(reload, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);
  const userByShopId = useMemo(() => {
    const m = {};
    for (const u of users) if (u.shop_id) m[u.shop_id] = u;
    return m;
  }, [users]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return shops;
    return shops.filter(s => [s.name, s.name_ta, s.phone, s.address, s.owner_name].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [shops, search]);

  const askDelete = async (s) => {
    const owner = userByShopId[s.id];
    if (!owner) return toast.error("Cannot find shop owner");
    const msg = lang === "ta" ? `${s.name} நீக்கவா?` : `Are you sure you want to delete ${s.name}?\nAction will be logged.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/users/${owner.id}`);
      toast.success(lang === "ta" ? "நீக்கப்பட்டது" : "Deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4 fade-up">
      <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3 flex-wrap" data-testid="shop-toolbar">
        <div className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl px-3 py-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#86868B]" />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="shop-search"
            placeholder={lang === "ta" ? "கடை / தொலைபேசி தேடு…" : "Search shop / phone…"}
            className="bg-transparent flex-1 outline-none text-sm font-semibold" />
        </div>
        {isSuperAdmin && (
          <button onClick={() => setAdding(true)} data-testid="shop-add-btn"
            className="bg-[#34C759] hover:bg-[#2BAE4D] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> {lang === "ta" ? "கடை சேர்" : "Add Shop"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shop-grid">
        {visible.map(s => {
          const owner = userByShopId[s.id];
          const dist = districtMap[s.district_id];
          const mapUrl = (s.lat && s.lng)
            ? `https://maps.google.com/?q=${s.lat},${s.lng}`
            : (s.address ? `https://maps.google.com/?q=${encodeURIComponent(s.address)}` : null);
          return (
            <div key={s.id} className="bg-white rounded-2xl p-5 soft-shadow flex flex-col gap-2" data-testid={`shop-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#34C759]">
                    {s.shop_type === "wholesaler" ? (lang === "ta" ? "மொத்த விற்பனையாளர்" : "Wholesaler") : (lang === "ta" ? "சில்லறை" : "Retailer")}
                  </div>
                  <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">
                    {lang === "ta" && s.name_ta ? s.name_ta : s.name}
                  </div>
                  {dist && <div className="text-xs text-[#86868B] truncate">{lang === "ta" && dist.name_ta ? dist.name_ta : dist.name}</div>}
                </div>
                {isSuperAdmin && owner && (
                  <div className="flex gap-1">
                    <button onClick={() => setEditing({ shop: s, owner })} data-testid={`shop-edit-${s.id}`}
                      className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]" title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => askDelete(s)} data-testid={`shop-delete-${s.id}`}
                      className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5 text-sm">
                {s.owner_name && <div className="text-[#86868B]">{lang === "ta" ? "உரிமையாளர்" : "Owner"}: <b className="text-[#1D1D1F]">{s.owner_name}</b></div>}
                <div className="flex items-center gap-2 text-[#1D1D1F]"><Phone className="w-3.5 h-3.5 text-[#86868B]" /> {s.phone || "—"}</div>
                {s.address && <div className="flex items-start gap-2 text-[#1D1D1F]"><MapPin className="w-3.5 h-3.5 text-[#86868B] mt-0.5" /> <span className="truncate">{s.address}</span></div>}
                {mapUrl && (
                  <a href={mapUrl} target="_blank" rel="noreferrer" data-testid={`shop-map-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-[#34C759] font-bold text-xs hover:underline">
                    <MapPin className="w-3.5 h-3.5" /> {lang === "ta" ? "வரைபடத்தில் காண்" : "View on Map"}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <div className="col-span-full text-center text-[#86868B] py-12 bg-white rounded-2xl">{lang === "ta" ? "கடை இல்ல" : "No shops"}</div>}
      </div>

      {editing && <ShopModal shop={editing.shop} owner={editing.owner} districts={districts} lang={lang}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {adding && <ShopModal shop={null} owner={null} districts={districts} lang={lang}
        onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}
    </div>
  );
};

const ShopModal = ({ shop, owner, districts, lang, onClose, onSaved }) => {
  const isNew = !shop;
  const [form, setForm] = useState({
    shop_name: shop?.name || "",
    shop_name_ta: shop?.name_ta || "",
    owner_name: shop?.owner_name || owner?.name || "",
    name_ta: owner?.name_ta || "",
    phone: shop?.phone || owner?.phone || "",
    address: shop?.address || owner?.address || "",
    district_id: shop?.district_id || (districts[0]?.id || ""),
    shop_type: shop?.shop_type || "retailer",
    lat: shop?.lat || "",
    lng: shop?.lng || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(p => {
      set("lat", p.coords.latitude);
      set("lng", p.coords.longitude);
      toast.success(lang === "ta" ? "இருப்பிடம் சேமிக்கப்பட்டது" : "Location captured");
    }, () => toast.error(lang === "ta" ? "இருப்பிடம் கிடைக்கல" : "Cannot get location"));
  };

  const submit = async () => {
    if (!form.shop_name.trim()) return toast.error(lang === "ta" ? "கடை பெயர் தேவை" : "Shop name required");
    if (!form.shop_name_ta.trim()) return toast.error(lang === "ta" ? "தமிழ் கடை பெயர் தேவை" : "Tamil shop name required");
    if (!form.phone.trim()) return toast.error(lang === "ta" ? "தொலைபேசி தேவை" : "Phone required");
    setSaving(true);
    try {
      const payload = {
        shop_name: form.shop_name, shop_name_ta: form.shop_name_ta,
        owner_name: form.owner_name,
        name_ta: form.name_ta || form.shop_name_ta,
        phone: form.phone, address: form.address,
        district_id: form.district_id || null,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
      };
      if (isNew) {
        await api.post("/users", { ...payload, name: form.owner_name || form.shop_name, role: form.shop_type, pin: "1234" });
      } else {
        await api.put(`/users/${owner.id}`, payload);
      }
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={isNew ? (lang === "ta" ? "புதிய கடை" : "New Shop") : (lang === "ta" ? "கடை திருத்து" : "Edit Shop")}
      sub={isNew ? "" : (shop?.name || "")} onClose={onClose} testid="shop-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label={lang === "ta" ? "கடை பெயர் (English)" : "Shop Name (English)"} value={form.shop_name} onChange={v => set("shop_name", v)} testid="shop-form-name" />
        <Field label={lang === "ta" ? "தமிழ் கடை பெயர்" : "Tamil Shop Name"} value={form.shop_name_ta} onChange={v => set("shop_name_ta", v)} testid="shop-form-name-ta" />
        <Field label={lang === "ta" ? "உரிமையாளர் பெயர்" : "Owner Name"} value={form.owner_name} onChange={v => set("owner_name", v)} testid="shop-form-owner" />
        <Field label={lang === "ta" ? "தொலைபேசி" : "Phone"} value={form.phone} onChange={v => set("phone", v)} testid="shop-form-phone" type="tel" />
        <div>
          <Lbl>{lang === "ta" ? "மாவட்டம்" : "District"}</Lbl>
          <select value={form.district_id} onChange={e => set("district_id", e.target.value)} data-testid="shop-form-district"
            className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold">
            <option value="">—</option>
            {districts.map(d => <option key={d.id} value={d.id}>{lang === "ta" && d.name_ta ? d.name_ta : d.name}</option>)}
          </select>
        </div>
        <div>
          <Lbl>{lang === "ta" ? "வகை" : "Type"}</Lbl>
          <select value={form.shop_type} onChange={e => set("shop_type", e.target.value)} disabled={!isNew} data-testid="shop-form-type"
            className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold disabled:opacity-60">
            <option value="retailer">{lang === "ta" ? "சில்லறை" : "Retailer"}</option>
            <option value="wholesaler">{lang === "ta" ? "மொத்த விற்பனையாளர்" : "Wholesaler"}</option>
          </select>
        </div>
      </div>
      <Field label={lang === "ta" ? "முகவரி" : "Address"} value={form.address} onChange={v => set("address", v)} testid="shop-form-address" multi />
      <div>
        <Lbl>{lang === "ta" ? "இருப்பிடம் (GPS)" : "GPS Location"}</Lbl>
        <div className="grid grid-cols-3 gap-2">
          <input value={form.lat} onChange={e => set("lat", e.target.value)} placeholder="Latitude" data-testid="shop-form-lat"
            className="bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
          <input value={form.lng} onChange={e => set("lng", e.target.value)} placeholder="Longitude" data-testid="shop-form-lng"
            className="bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
          <button onClick={useMyLocation} data-testid="shop-form-use-location"
            className="bg-[#34C759] hover:bg-[#2BAE4D] text-white rounded-xl px-3 py-2 font-bold text-xs flex items-center justify-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {lang === "ta" ? "இங்கே" : "Use here"}
          </button>
        </div>
      </div>
      <SaveBtn saving={saving} onClick={submit} lang={lang} testid="shop-form-save" />
    </ModalShell>
  );
};

// =================== PRODUCT TAB ===================
const ProductTab = ({ lang, isSuperAdmin }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const reload = () => { api.get("/products").then(r => setProducts(r.data || [])); };
  useEffect(reload, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => [p.name, p.name_ta, p.name_en, p.sku, p.category].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [products, search]);

  const askDelete = async (p) => {
    const msg = lang === "ta" ? `${p.name} நீக்கவா?` : `Are you sure you want to delete ${p.name}?\nAction will be logged.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success(lang === "ta" ? "நீக்கப்பட்டது" : "Deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4 fade-up">
      <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3 flex-wrap" data-testid="prod-toolbar">
        <div className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl px-3 py-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#86868B]" />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="prod-search"
            placeholder={lang === "ta" ? "பொருள் தேடு…" : "Search product…"}
            className="bg-transparent flex-1 outline-none text-sm font-semibold" />
        </div>
        {isSuperAdmin && (
          <button onClick={() => setAdding(true)} data-testid="prod-add-btn"
            className="bg-[#5856D6] hover:bg-[#4946C0] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> {lang === "ta" ? "சேர்" : "Add Product"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prod-grid">
        {visible.map(p => (
          <div key={p.id} className="bg-white rounded-2xl p-5 soft-shadow flex flex-col gap-2" data-testid={`prod-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {p.category && <div className="text-[10px] uppercase tracking-wider font-bold text-[#5856D6]">{p.category}</div>}
                <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">{lang === "ta" && p.name_ta ? p.name_ta : p.name_en || p.name}</div>
                <div className="text-xs text-[#86868B]">SKU: {p.sku}</div>
              </div>
              {isSuperAdmin && (
                <div className="flex gap-1">
                  <button onClick={() => setEditing(p)} data-testid={`prod-edit-${p.id}`}
                    className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => askDelete(p)} data-testid={`prod-delete-${p.id}`}
                    className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            {(p.price !== undefined && p.price !== null) && (
              <div className="text-sm text-[#86868B]">{lang === "ta" ? "சில்லறை விலை" : "Retail Price"}: <b className="text-[#1D1D1F]">₹{p.price}</b></div>
            )}
          </div>
        ))}
        {visible.length === 0 && <div className="col-span-full text-center text-[#86868B] py-12 bg-white rounded-2xl">{lang === "ta" ? "பொருள் இல்ல" : "No products"}</div>}
      </div>

      {editing && <ProductModal product={editing} lang={lang}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {adding && <ProductModal product={null} lang={lang}
        onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}
    </div>
  );
};

const ProductModal = ({ product, lang, onClose, onSaved }) => {
  const isNew = !product;
  const [form, setForm] = useState({
    name: product?.name || "",
    name_en: product?.name_en || product?.name || "",
    name_ta: product?.name_ta || "",
    sku: product?.sku || "",
    price: product?.price ?? "",
    wholesaler_price: product?.wholesaler_price ?? "",
    category: product?.category || "",
    unit: product?.unit || "box",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name_en.trim()) return toast.error(lang === "ta" ? "ஆங்கில பெயர் தேவை" : "English name required");
    if (!form.sku.trim()) return toast.error("SKU required");
    setSaving(true);
    try {
      const payload = {
        name: form.name_en, name_en: form.name_en, name_ta: form.name_ta || null,
        sku: form.sku, price: parseFloat(form.price) || 0,
        wholesaler_price: form.wholesaler_price === "" ? null : parseFloat(form.wholesaler_price),
        category: form.category || null, unit: form.unit,
      };
      if (isNew) await api.post("/products", payload);
      else await api.put(`/products/${product.id}`, payload);
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={isNew ? (lang === "ta" ? "புதிய பொருள்" : "New Product") : (lang === "ta" ? "பொருள் திருத்து" : "Edit Product")}
      sub={isNew ? "" : product?.sku || ""} onClose={onClose} testid="prod-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label={lang === "ta" ? "பெயர் (English)" : "Name (English)"} value={form.name_en} onChange={v => set("name_en", v)} testid="prod-form-name" />
        <Field label={lang === "ta" ? "தமிழ் பெயர்" : "Tamil Name"} value={form.name_ta} onChange={v => set("name_ta", v)} testid="prod-form-name-ta" />
        <Field label="SKU" value={form.sku} onChange={v => set("sku", v)} testid="prod-form-sku" />
        <Field label={lang === "ta" ? "வகை" : "Category"} value={form.category} onChange={v => set("category", v)} testid="prod-form-category" />
        <Field label={lang === "ta" ? "சில்லறை விலை" : "Retail Price"} value={form.price} onChange={v => set("price", v)} testid="prod-form-price" type="number" />
        <Field label={lang === "ta" ? "மொத்த விலை" : "Wholesaler Price"} value={form.wholesaler_price} onChange={v => set("wholesaler_price", v)} testid="prod-form-wprice" type="number" />
      </div>
      <SaveBtn saving={saving} onClick={submit} lang={lang} testid="prod-form-save" />
    </ModalShell>
  );
};

// =================== Reusable form bits ===================
const ModalShell = ({ title, sub, onClose, testid, children }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
    <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto soft-shadow-md fade-up" data-testid={testid} onClick={e => e.stopPropagation()}>
      <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between z-10">
        <div>
          <h3 className="heading-font font-bold text-xl">{title}</h3>
          {sub && <div className="text-xs text-[#86868B] mt-1">{sub}</div>}
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  </div>
);

const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{children}</label>;

const Field = ({ label, value, onChange, testid, type = "text", multi = false }) => (
  <div className={multi ? "col-span-2" : ""}>
    <Lbl>{label}</Lbl>
    {multi ? (
      <textarea rows="2" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold resize-none" />
    ) : (
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" />
    )}
  </div>
);

const SaveBtn = ({ saving, onClick, lang, testid }) => (
  <button onClick={onClick} disabled={saving} data-testid={testid}
    className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
    <Save className="w-4 h-4" /> {saving ? "…" : (lang === "ta" ? "சேமி" : "Save")}
  </button>
);
